import React, { useEffect, useMemo, useCallback, useRef } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { BaseForm } from 'components'
import _ from 'lodash'

import ResponsiveBox, { Row, Col, Item, Location } from 'devextreme-react/responsive-box'
import Button from 'devextreme-react/button'
import CheckBox from 'devextreme-react/check-box'

import { IxField } from 'components/forms/field'

import { useAppDispatch } from 'store'
import { RootState } from 'slices'
import { setFilterValues, setFilterLookupValue, setValuesFromDB, setDirectLoad, setLoad, setActiveDivRef } from 'slices/uml-slice.gen'
import utils from 'utils'
import { setGridFilters } from 'slices/pages-slice'

import { setMaxLabelWidth } from 'helpers/set-max-label-width'
import { keyboardControl } from 'helpers'
import { useIxFocusElement } from 'hooks'

import { IxLookupProvider, useIxLookupContext } from 'contexts/lookup-context'

import {
    DateInput,
} from 'components/forms'


import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

const _t = (s: string) => s
const labelsMap: {[k: string]: string} = {
    STICHTAG: _t('None'),
}

const lookupFields: any[] = [
]


interface IAutoUpdateForm {
    pageName?: string,
    itemsData: any[],
    lastFocusedElement: any,
    divRef: any
}

export const AutoUpdateForm = ({ pageName, itemsData, lastFocusedElement, divRef }: IAutoUpdateForm) => {
    const dispatch = useAppDispatch()

    const { values, setFieldValue } = useFormikContext<any>()
    const { setFieldValue: setLookupFieldValue, fieldHasCallbacks } = useIxLookupContext()
    const activeDivRef = useSelector((state: RootState) => state.umlSlice.activeDivRef)

    const filterValues = useSelector((state: RootState) => state.umlSlice.filterValues)
    const filterLookupValues = useSelector((state: RootState) => state.umlSlice.filterLookupValues)
    const valuesFromDB = useSelector((state: RootState) => state.umlSlice.valuesFromDB)

    const prevFilterLookupValues = useRef<any>({})
    const prevFilterValues = useRef<any>({})
    const firstRender = useRef<any>(true)


    useIxFocusElement(values, itemsData, lastFocusedElement, activeDivRef, divRef)

    useEffect(() => {
        for(const [key, value] of Object.entries(filterLookupValues)) {
            if (!_.isEqual(prevFilterLookupValues.current[key], value) && fieldHasCallbacks(key)) {
                setLookupFieldValue(key, (value as any), false, true)
                prevFilterLookupValues.current[key] = value
            }
        }
    }, [filterLookupValues, setLookupFieldValue, fieldHasCallbacks])

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false
            if (!pageName) { return }
        }

        if (!_.isEqual(prevFilterValues.current, filterValues)) { return }

        const vals = Object.keys(values)
            .filter(k => !lookupFields.includes(k))
            .reduce((obj: any, key: string) => {
                obj[key] = values[key]
                return obj
            }, {})

        const _filterValues = Object.keys(filterValues)
            .filter(k => !lookupFields.includes(k))
            .reduce((obj: any, key: string) => {
                obj[key] = filterValues[key]
                return obj
            }, {})

        if (!_.isEqual(_filterValues, vals)) {
            dispatch(setFilterValues(vals))
            prevFilterValues.current = vals
        }
    }, [dispatch, values, filterValues, pageName])

    useEffect(() => {
        for(const [key, value] of Object.entries(filterValues)) {
            if ((valuesFromDB || lookupFields.indexOf(key) === -1) && prevFilterValues.current[key] !== value) {
                setFieldValue(key, value, false)
            }
        }

        dispatch(setValuesFromDB(false))
        prevFilterValues.current = filterValues
    }, [filterValues, setFieldValue, dispatch, valuesFromDB])

    useEffect(() => {
        pageName && dispatch(setGridFilters({ name: pageName, filterValues, filterLookupValues }))
    }, [dispatch, filterValues, filterLookupValues, pageName])

    return null
}

export type IIx1033FilterContent = {
    pageName?: string,
    loadingEnabled: boolean,
    visible: boolean,
}

export const Ix1033FilterContent = ({ pageName, loadingEnabled, visible }: IIx1033FilterContent) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const { values } = useFormikContext<any>()

    const divRef = useRef<HTMLDivElement>(null)
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))

    const itemsData = useMemo(() => ([
        { name: 'STICHTAG', index: 1, type: 'field' },

        { name: 'DIRECT_LOAD', index: 0, type: 'field' },
        { name: t('global:button.load'), index: 0, type: 'button' }
    ]), [t])

    const lastFocusedElement = useRef<any>(null)
    const onKeyboardControl = keyboardControl(itemsData, t, divRef, lastFocusedElement)

    const directLoad = useSelector((state: RootState) => state.umlSlice.directLoad)


    const prevWidth = useRef<any>(0)

    const onDirectLoadChange = useCallback((e: any) => {
        dispatch(setDirectLoad(e.value))
    }, [dispatch])

    useEffect(() => {
            const filterElement: HTMLElement = (divRef.current as any).closest('.dx-drawer-panel-content') || (divRef.current as any).closest('.dx-drawer-content')
            filterElement?.addEventListener('keydown', onKeyboardControl)

            return () => {
                filterElement?.removeEventListener('keydown', onKeyboardControl)
            }
        },[onKeyboardControl])

    useEffect(() => {
        const filterElement: HTMLElement = (divRef.current as any).closest('.dx-drawer-panel-content') || (divRef.current as any).closest('.dx-drawer-content')

        const setPrevFocusedElement = (e: any) => {
            const filterButton = e.target.closest('.dx-drawer-panel-content')?.querySelector('[aria-label="filter"]')
            dispatch(setActiveDivRef(e.target.closest('.dx-drawer-content') || e.target.closest('.dx-drawer-panel-content')))

            if (e.target.tagName === 'DIV' && !e.target.className.includes('dropdowneditor') && !e.target.className.includes('button') && !e.target.closest('.dx-radiobutton') && filterButton) {
                setTimeout(() => {
                    e.target.closest('.dx-drawer-panel-content').querySelector('[aria-label="filter"]').focus()
                    e.target.closest('.dx-drawer-panel-content')?.querySelector('[aria-label="filter"]').classList.remove('dx-state-focused')
                }, 0)
            }

            if (e.target.tagName === 'INPUT' || e.target.tagName === 'I' || (typeof e.target.className === 'string' && e.target.className.includes('dropdowneditor'))) {
                lastFocusedElement.current = e.target
                filterElement?.querySelector('.dx-state-focused.dx-radiobutton')?.classList.remove('dx-state-focused')
                filterElement?.querySelector('.dx-checkbox.dx-state-focused')?.classList.remove('dx-state-focused')
            }
        }
        filterElement?.addEventListener('mousedown', setPrevFocusedElement)

        return () => {
            filterElement?.removeEventListener('mousedown', setPrevFocusedElement)
        }
    }, [dispatch])

    useEffect(() => {
        if (visible) {
            const formElement: any = divRef.current

            if (formFieldType === 'label-inside-max-field-width' || formFieldType === 'label-left-max-field-width') {
                setTimeout(() => {
                    setMaxLabelWidth(formElement)
                }, 500)
            }
            else {
                formElement?.style.removeProperty('--max-labels-width')
            }
        }

    },[visible, formFieldType])

    const screenByWidth = useCallback((_: any) => {
        const width: any = divRef.current?.getBoundingClientRect().width || 0

        if (width < 500) {
            return 'xs'
        } else {
            return 'lg'
        }
    }, [])

    const onOptionChanged = useCallback((e: any) => {
        const width = divRef.current?.getBoundingClientRect().width

        if (prevWidth.current !== width) {
            e.component.repaint()
            prevWidth.current = width
        }
    },[])

    const onLookupFieldChange = useCallback((name: string, value: any) => {
        dispatch(setFilterLookupValue({ name, value, formValue: values[name] }))
    }, [dispatch, values])

    return (
        <div ref={ divRef } style={ visible ? {} : { height: 0, overflow: 'hidden' } }>
            <IxLookupProvider onFieldChanged={ onLookupFieldChange }>
                <AutoUpdateForm pageName={ pageName } itemsData={ itemsData } lastFocusedElement={lastFocusedElement} divRef={divRef}/>
                <ResponsiveBox screenByWidth={ screenByWidth } onOptionChanged={(e) => onOptionChanged(e)}>
                    {/* layout goes here */}
                    <Row />
                    <Row screen = 'xs'/>

                    <Col />
                    <Col screen='sm md lg'/>
                    <Col screen='md lg'/>
                    <Col screen='md lg'/>
                    <Col screen='sm md lg' ratio={ 0.5 }/>

                    <Item>
                        <Location row={ 0 } col={ 0 } colspan={ 4 } screen='md lg' />
                        <Location row={ 0 } col={ 0 } colspan={ 1 } screen='sm' />
                        <Location row={ 0 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='item-div'>
                            <IxField
                                as={ DateInput }
                                name='STICHTAG'
                                label={ t(labelsMap['STICHTAG']) }
                                active={ loadingEnabled }
                            />
                        </div>
                    </Item>

                    <Item>
                        <Location row={ 0 } col={ 4 } colspan={ 1 } rowspan={ 1 } screen='md lg' />
                        <Location row={ 0 } col={ 2 } colspan={ 1 } rowspan={ 1 } screen='sm' />
                        <Location row={ 1 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='rb-item mb-1'>
                            <div className='mb-1'>
                                <IxField
                                    as={ CheckBox }
                                    name='DIRECT_LOAD'
                                    text={ t('global:label.directLoad') }
                                    onValueChanged={ onDirectLoadChange }
                                    value={ directLoad }
                                />
                            </div>
                            <Button disabled={ directLoad } width='100%' text={t('global:button.load')} onClick={ () => dispatch(setLoad(true)) }/>
                        </div>
                    </Item>
                </ResponsiveBox>
            </IxLookupProvider>
        </div>
    )
}

export type IIx1033Filter = {
    pageName?: string,
    loadingEnabled?: boolean,
    visible?: boolean,
}

export const Ix1033Filter = ({ pageName, loadingEnabled=true, visible=true }: IIx1033Filter) => {
    const formik = useMemo(() => ({
        initialValues: {
            "STICHTAG": utils.sysdate()
        },
        enableReinitialize: true,
        jsonSchema: null,
        avjOptions: {
            coerceTypes: true,
            $data: true,
        },
    }), [])

    return (
        <BaseForm {...formik}>
            <Ix1033FilterContent pageName={ pageName } loadingEnabled={ loadingEnabled } visible={ visible } />
        </BaseForm>
    )
}
